/* OROMA: Vollbildmenü – externe Steuerung für Storefront und Theme-Editor */
(() => {
  const MENU_SELECTOR = '#header-component .menu-drawer-container';
  const SUMMARY_SELECTOR = ':scope > summary[aria-label="Menü"]';

  const addContact = () => {
    const drawer = document.querySelector('#header-component .menu-drawer');
    if (!drawer) return false;
    if (!drawer.querySelector('.oroma-menu-contact')) {
      const contact = document.createElement('aside');
      contact.className = 'oroma-menu-contact';
      contact.setAttribute('aria-label', 'Kontakt');
      contact.innerHTML = '<h2 class="oroma-menu-contact__title">Kontakt</h2><p class="oroma-menu-contact__text">OROMA GmbH<br>Ankenbüel 10<br>8126 Zumikon</p><a class="oroma-menu-contact__link" href="mailto:info@oroma.ch">info@oroma.ch</a>';
      drawer.appendChild(contact);
    }
    return true;
  };

  const fixVerkaufsstellenLink = () => {
    const links = document.querySelectorAll('#header-component a[href*="/pages/verkaufsstellen"]');
    if (!links.length) return false;

    const currentUrl = new URL(window.location.href);
    const previewParameters = ['preview_theme_id', '_ab', '_fd', '_sc', 'pb'];

    links.forEach((link) => {
      const url = new URL(link.href, window.location.origin);
      if (url.pathname !== '/pages/verkaufsstellen') return;

      url.searchParams.set('view', 'verkaufsstellen');
      previewParameters.forEach((parameter) => {
        if (currentUrl.searchParams.has(parameter) && !url.searchParams.has(parameter)) {
          url.searchParams.set(parameter, currentUrl.searchParams.get(parameter));
        }
      });

      link.href = url.pathname + url.search + url.hash;
    });

    return true;
  };

  const installMenuController = () => {
    const details = document.querySelector(MENU_SELECTOR);
    const originalSummary = details?.querySelector(SUMMARY_SELECTOR);
    if (!details || !originalSummary) return false;
    if (originalSummary.dataset.oromaMenuController === 'asset-v7') return true;

    const summary = originalSummary.cloneNode(true);
    summary.removeAttribute('on:click');
    summary.dataset.oromaMenuController = 'asset-v7';
    summary.setAttribute('aria-expanded', String(details.open));
    originalSummary.replaceWith(summary);
    document.documentElement.classList.toggle('oroma-menu-open', details.open);

    summary.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const willOpen = !details.open;
      details.open = willOpen;
      document.documentElement.classList.toggle('oroma-menu-open', willOpen);
      summary.setAttribute('aria-expanded', String(willOpen));
    }, true);

    return true;
  };

  const closeMenuWithEscape = (event) => {
    if (event.key !== 'Escape') return;
    const details = document.querySelector(MENU_SELECTOR);
    const summary = details?.querySelector(SUMMARY_SELECTOR);
    if (!details?.open) return;

    details.open = false;
    document.documentElement.classList.remove('oroma-menu-open');
    summary?.setAttribute('aria-expanded', 'false');
    summary?.focus();
  };

  const parseColour = (value) => {
    if (!value) return null;
    const colour = value.trim();

    if (colour.startsWith('#')) {
      const hex = colour.slice(1);
      const full = hex.length === 3 ? hex.split('').map((character) => character + character).join('') : hex;
      if (full.length < 6) return null;
      return {
        r: Number.parseInt(full.slice(0, 2), 16),
        g: Number.parseInt(full.slice(2, 4), 16),
        b: Number.parseInt(full.slice(4, 6), 16),
        a: 1
      };
    }

    const match = colour.match(/rgba?\(([^)]+)\)/i);
    if (!match) return null;
    const parts = match[1].split(',').map((part) => Number.parseFloat(part.trim()));
    return { r: parts[0], g: parts[1], b: parts[2], a: parts[3] ?? 1 };
  };

  const colourLuminance = ({ r, g, b }) => {
    const channels = [r, g, b].map((channel) => {
      const value = channel / 255;
      return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
    });
    return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
  };

  const imageSampleCache = new Map();

  const positionRatio = (value, axis) => {
    const token = String(value || '').toLowerCase();
    if (token.includes('%')) return Math.max(0, Math.min(1, Number.parseFloat(token) / 100));
    if (token === 'left' || token === 'top') return 0;
    if (token === 'right' || token === 'bottom') return 1;
    if (token === 'center') return 0.5;
    return axis === 'x' ? 0.5 : 0.5;
  };

  const sampleMediaLuminance = (media, x, y) => {
    try {
      const rect = media.getBoundingClientRect();
      const sourceWidth = media.naturalWidth || media.videoWidth || media.width;
      const sourceHeight = media.naturalHeight || media.videoHeight || media.height;
      if (!sourceWidth || !sourceHeight || rect.width <= 0 || rect.height <= 0) return null;

      const style = window.getComputedStyle(media);
      const fit = style.objectFit || 'fill';
      let scaleX = rect.width / sourceWidth;
      let scaleY = rect.height / sourceHeight;
      let renderedWidth = rect.width;
      let renderedHeight = rect.height;

      if (fit === 'cover' || fit === 'contain' || fit === 'scale-down') {
        let scale = fit === 'cover' ? Math.max(scaleX, scaleY) : Math.min(scaleX, scaleY);
        if (fit === 'scale-down') scale = Math.min(1, scale);
        scaleX = scale;
        scaleY = scale;
        renderedWidth = sourceWidth * scale;
        renderedHeight = sourceHeight * scale;
      } else if (fit === 'none') {
        scaleX = 1;
        scaleY = 1;
        renderedWidth = sourceWidth;
        renderedHeight = sourceHeight;
      }

      const positions = (style.objectPosition || '50% 50%').trim().split(/\s+/);
      const positionX = positionRatio(positions[0], 'x');
      const positionY = positionRatio(positions[1] || positions[0], 'y');
      const offsetX = (rect.width - renderedWidth) * positionX;
      const offsetY = (rect.height - renderedHeight) * positionY;
      const sourceX = (x - rect.left - offsetX) / scaleX;
      const sourceY = (y - rect.top - offsetY) / scaleY;

      if (sourceX < 0 || sourceY < 0 || sourceX > sourceWidth || sourceY > sourceHeight) return null;

      const cacheKey = [
        media.currentSrc || media.src || 'media',
        Math.round(sourceX / 24),
        Math.round(sourceY / 24),
        Math.round(sourceWidth / 100),
        Math.round(sourceHeight / 100)
      ].join('|');

      if (imageSampleCache.has(cacheKey)) return imageSampleCache.get(cacheKey);

      const canvas = document.createElement('canvas');
      canvas.width = 12;
      canvas.height = 12;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      const radiusX = Math.max(2, 28 / Math.max(scaleX, 0.01));
      const radiusY = Math.max(2, 28 / Math.max(scaleY, 0.01));

      context.drawImage(
        media,
        Math.max(0, sourceX - radiusX),
        Math.max(0, sourceY - radiusY),
        Math.min(sourceWidth, radiusX * 2),
        Math.min(sourceHeight, radiusY * 2),
        0,
        0,
        canvas.width,
        canvas.height
      );

      const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
      let total = 0;
      let weight = 0;

      for (let index = 0; index < pixels.length; index += 4) {
        const alpha = pixels[index + 3] / 255;
        if (alpha <= 0.05) continue;
        total += colourLuminance({ r: pixels[index], g: pixels[index + 1], b: pixels[index + 2] }) * alpha;
        weight += alpha;
      }

      const sample = weight
        ? { luminance: total / weight, alpha: Math.max(0, Math.min(1, weight / (canvas.width * canvas.height))) }
        : null;
      if (sample !== null) imageSampleCache.set(cacheKey, sample);
      if (imageSampleCache.size > 200) imageSampleCache.delete(imageSampleCache.keys().next().value);
      return sample;
    } catch (error) {
      return null;
    }
  };

  const sectionAtPoint = (x, y, layers) => {
    const fromLayers = layers.map((element) => element.closest('.shopify-section, section')).find(Boolean);
    if (fromLayers) return fromLayers;

    return [...document.querySelectorAll('.shopify-section, main > section')].find((section) => {
      const rect = section.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    });
  };

  const luminanceAtPoint = (x, y) => {
    const header = document.querySelector('#header-component');
    const layers = document.elementsFromPoint(x, y).filter((element) => element !== header && !header?.contains(element));
    const section = sectionAtPoint(x, y, layers);
    const candidates = [...new Set([...layers, ...(section ? [section] : [])])];

    let overlay = null;
    let solidBackground = null;
    let pageBackground = null;

    for (const element of candidates) {
      for (const pseudo of [null, '::before', '::after']) {
        const style = window.getComputedStyle(element, pseudo);
        const colour = parseColour(style.backgroundColor);
        if (!colour || colour.a <= 0.03) continue;

        if (colour.a >= 0.92) {
          const value = colourLuminance(colour);
          const isPageBackground = element === document.body || element === document.documentElement;
          const isSectionBackground = element.classList?.contains('section-background');

          if (isPageBackground) {
            if (pageBackground === null) pageBackground = value;
          } else if (isSectionBackground) {
            solidBackground = value;
          } else if (solidBackground === null) {
            solidBackground = value;
          }
        }

        if (colour.a < 0.92 && overlay === null) {
          overlay = { luminance: colourLuminance(colour), alpha: colour.a };
        }
      }
    }

    if (solidBackground === null) solidBackground = pageBackground;

    const possibleMedia = [
      ...layers.filter((element) => element.matches?.('img, video, canvas')),
      ...(section ? [...section.querySelectorAll('img, video, canvas')] : [])
    ];

    const mediaCandidates = [...new Set(possibleMedia)].filter((media) => {
      const rect = media.getBoundingClientRect();
      const style = window.getComputedStyle(media);
      return rect.width > 0
        && rect.height > 0
        && x >= rect.left
        && x <= rect.right
        && y >= rect.top
        && y <= rect.bottom
        && style.display !== 'none'
        && style.visibility !== 'hidden'
        && Number.parseFloat(style.opacity || '1') > 0.05;
    });

    let luminance = null;
    let mediaSample = null;
    for (const media of mediaCandidates) {
      mediaSample = sampleMediaLuminance(media, x, y);
      if (mediaSample !== null) {
        luminance = mediaSample.luminance;
        break;
      }
    }

    if (luminance !== null && mediaSample && solidBackground !== null && mediaSample.alpha < 0.98) {
      luminance = luminance * mediaSample.alpha + solidBackground * (1 - mediaSample.alpha);
    }

    if (luminance === null && solidBackground !== null) luminance = solidBackground;

    if (luminance !== null && overlay) {
      luminance = luminance * (1 - overlay.alpha) + overlay.luminance * overlay.alpha;
    }

    if (luminance === null && section) {
      const textColours = [...section.querySelectorAll('h1, h2, h3, h4, h5, p, a, span')]
        .map((element) => {
          const rect = element.getBoundingClientRect();
          const colour = parseColour(window.getComputedStyle(element).color);
          return rect.width > 0 && rect.height > 0 && colour && colour.a > 0.5 ? colourLuminance(colour) : null;
        })
        .filter((value) => value !== null);

      if (textColours.length) {
        textColours.sort((a, b) => a - b);
        const medianTextLuminance = textColours[Math.floor(textColours.length / 2)];
        luminance = medianTextLuminance > 0.55 ? 0.12 : 0.88;
      }
    }

    if (luminance === null) {
      const reference = section || layers[0];
      const style = reference ? window.getComputedStyle(reference) : null;
      const foreground = parseColour(style?.getPropertyValue('--color-foreground')) || parseColour(style?.color);
      if (foreground) luminance = colourLuminance(foreground) < 0.45 ? 0.88 : 0.12;
    }

    return luminance;
  };

  const applyAdaptiveColour = (header, className, x, y) => {
    const luminance = luminanceAtPoint(x, y);
    if (luminance === null) return;
    const wasBrown = header.classList.contains(className);
    const useBrown = wasBrown ? luminance > 0.43 : luminance > 0.56;
    header.classList.toggle(className, useBrown);
  };

  const BROWN_LUMINANCE = colourLuminance({ r: 35, g: 22, b: 0 });
  const BEIGE_LUMINANCE = colourLuminance({ r: 241, g: 231, b: 204 });

  const contrastRatio = (foreground, background) => {
    const lighter = Math.max(foreground, background);
    const darker = Math.min(foreground, background);
    return (lighter + 0.05) / (darker + 0.05);
  };

  const contrastScore = (foreground, backgrounds) => {
    const ratios = backgrounds
      .map((background) => contrastRatio(foreground, background))
      .sort((a, b) => a - b);
    const criticalCount = Math.max(2, Math.ceil(ratios.length * 0.4));
    const critical = ratios.slice(0, criticalCount);
    const criticalAverage = critical.reduce((total, ratio) => total + ratio, 0) / critical.length;
    return criticalAverage * 0.65 + ratios[0] * 0.35;
  };

  const applyAdaptiveMenuColour = (header, centerX, centerY) => {
    const xOffsets = [-18, -9, 0, 9, 18];
    const yOffsets = [-7, 7];
    const luminances = [];

    yOffsets.forEach((offsetY) => {
      xOffsets.forEach((offsetX) => {
        const x = Math.max(1, Math.min(window.innerWidth - 1, centerX + offsetX));
        const y = Math.max(1, Math.min(window.innerHeight - 1, centerY + offsetY));
        const luminance = luminanceAtPoint(x, y);
        if (luminance !== null) luminances.push(luminance);
      });
    });

    if (!luminances.length) return;

    const brownScore = contrastScore(BROWN_LUMINANCE, luminances);
    const beigeScore = contrastScore(BEIGE_LUMINANCE, luminances);
    const wasBrown = header.classList.contains('oroma-menu--brown');
    const scoreDifference = brownScore - beigeScore;
    const useBrown = Math.abs(scoreDifference) < 0.12 ? wasBrown : scoreDifference > 0;
    const darkest = Math.min(...luminances);
    const lightest = Math.max(...luminances);
    const mixedBackground = lightest - darkest > 0.35 && darkest < 0.46 && lightest > 0.56;

    header.classList.toggle('oroma-menu--brown', useBrown);
    header.classList.toggle('oroma-menu--mixed', mixedBackground);
  };

  const updateHeaderContrast = () => {
    const header = document.querySelector('#header-component');
    const summary = header?.querySelector('summary.header__icon--menu');
    const logo = header?.querySelector('.header-logo');
    const details = header?.querySelector('.menu-drawer-container');
    if (!header || !summary) return false;

    if (details?.open) {
      header.classList.remove('oroma-menu--brown', 'oroma-menu--mixed', 'oroma-logo--brown', 'oroma-header--brown');
      return true;
    }

    const summaryRect = summary.getBoundingClientRect();
    const summaryIsVisible = summaryRect.width > 0
      && summaryRect.height > 0
      && summaryRect.bottom > 0
      && summaryRect.top < window.innerHeight;
    const summaryX = summaryIsVisible
      ? Math.max(1, Math.min(window.innerWidth - 1, summaryRect.left + summaryRect.width / 2))
      : Math.round(window.innerWidth / 2);
    const summaryY = summaryIsVisible
      ? Math.max(1, Math.min(window.innerHeight - 1, summaryRect.top + summaryRect.height / 2))
      : 46;
    applyAdaptiveMenuColour(header, summaryX, summaryY);

    // OROMA: Logo und Menü-Icon verwenden immer gemeinsam dieselbe Kontrastfarbe.
    if (logo) {
      header.classList.toggle('oroma-logo--brown', header.classList.contains('oroma-menu--brown'));
    }

    header.classList.remove('oroma-header--brown');
    return true;
  };

  const alignContactWithLastMenuItem = () => {
    const drawer = document.querySelector('#header-component .menu-drawer');
    const contact = drawer?.querySelector('.oroma-menu-contact');
    const items = drawer ? [...drawer.querySelectorAll('.menu-drawer__list-item--flat')] : [];

    if (!drawer || !contact) return false;

    if (window.matchMedia('(max-width: 749px)').matches || !items.length) {
      ['position', 'top', 'right', 'left', 'padding-bottom'].forEach((property) => {
        contact.style.removeProperty(property);
      });
      return true;
    }

    const lastItem = items[items.length - 1];
    const drawerRect = drawer.getBoundingClientRect();
    const itemRect = lastItem.getBoundingClientRect();
    const itemStyle = window.getComputedStyle(lastItem);
    const drawerStyle = window.getComputedStyle(drawer);
    let itemTranslateY = 0;

    if (itemStyle.transform && itemStyle.transform !== 'none') {
      try {
        itemTranslateY = new DOMMatrixReadOnly(itemStyle.transform).m42;
      } catch (error) {
        itemTranslateY = 0;
      }
    }

    const topPadding = Number.parseFloat(drawerStyle.paddingTop) || 0;
    const alignedTop = Math.round(itemRect.bottom - itemTranslateY - drawerRect.top - topPadding);
    const rightPadding = Number.parseFloat(drawerStyle.paddingRight) || 0;

    contact.style.setProperty('position', 'absolute', 'important');
    contact.style.setProperty('top', alignedTop + 'px', 'important');
    contact.style.setProperty('right', rightPadding + 'px', 'important');
    contact.style.setProperty('left', 'auto', 'important');
    contact.style.setProperty('padding-bottom', '0px', 'important');
    return true;
  };

  const initialise = () => {
    addContact();
    fixVerkaufsstellenLink();
    installMenuController();
    alignContactWithLastMenuItem();
    updateHeaderContrast();
  };

  const boot = () => {
    initialise();
  };

  if (document.documentElement.dataset.oromaMenuAsset !== 'v8') {
    document.documentElement.dataset.oromaMenuAsset = 'v8';
    document.addEventListener('keydown', closeMenuWithEscape);
    document.addEventListener('shopify:section:load', boot);
    window.addEventListener('pageshow', boot);
    window.addEventListener('popstate', boot);
    window.setInterval(initialise, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
